import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../util/Toast';
import { useSettings } from '../util/SettingsProvider';
export const Offers = () => {
  const [offers, setOffers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  const settings = useSettings();
  useEffect(() => { document.title = `Offers | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  useEffect(() => {
    const fetchOffers = async () => {
      const token = getCookie('authToken');
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_URL || '';

        const response = await axios.get(`${baseUrl}/api/offers/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data && response.data.offers) {
          setOffers(response.data.offers);
        }
      } catch (error) {
        console.error('Error fetching offers:', error);
        toast.error(error.response?.data?.msg || "An unexpected error occurred.");
        if (error.response && error.response.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, [navigate]);

  // Filter offers based on backend property fields (id, name, category)
  const filteredOffers = offers.filter(
    (offer) =>
      (offer.name && offer.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (offer.id && offer.id.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (offer.category && offer.category.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Helper to render logo whether it's an image URL or an emoji string (e.g. "🎯")
  const renderLogo = (logo, name) => {
    const isUrl = typeof logo === 'string' && (logo.startsWith('http') || logo.startsWith('/'));

    if (isUrl) {
      return (
        <img
          src={logo}
          alt={name || 'Offer'}
          loading="lazy"
          decoding="async"
          className="w-12 h-12 bg-zinc-950 rounded-2xl object-contain p-1 border border-zinc-800 shadow-inner group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(name || 'Offer') + '&background=059669&color=fff';
          }}
        />
      );
    }

    return (
      <div className="w-12 h-12 bg-zinc-950 rounded-2xl flex items-center justify-center text-xl border border-zinc-800 shadow-inner group-hover:scale-110 group-hover:border-emerald-500/30 transition-all duration-300">
        {logo || '🎯'}
      </div>
    );
  };

  return (
    <div className="w-full pt-16 sm:pt-24 lg:pt-28 pb-16 px-4 sm:px-8 lg:pl-80 relative overflow-hidden font-sans">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        .animate-spring-in {
          animation: springIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 8s ease-in-out infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }
      `}</style>

      {/* Fluid ambient background lighting */}
      <div className="absolute top-10 left-1/3 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />

      <div className="max-w-[1400px] space-y-6 relative z-10">

        {/* Header & Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-spring-in">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-100">
              Available Offers
            </h2>
            <p className="text-zinc-400 text-sm mt-1 font-medium">
              Browse and promote top-performing offers.
            </p>
          </div>

          <div className="relative w-full sm:w-80 group">
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 group-focus-within:text-emerald-400 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-zinc-900/90 border border-zinc-800 rounded-2xl text-zinc-100 placeholder-zinc-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-4 focus:ring-emerald-500/10 transition-all micro-interaction shadow-lg shadow-black/40"
            />
          </div>
        </div>

        {/* Content States */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-zinc-500 text-sm font-semibold tracking-wide">Loading offers...</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <div className="text-center py-20 bg-zinc-900/50 rounded-[2.5rem] border border-zinc-800 backdrop-blur-sm animate-spring-in">
            <p className="text-zinc-400 text-base font-semibold">No offers found matching "{searchQuery}"</p>
          </div>
        ) : (
          <>
            {/* Mobile Cards View */}
            <div className="grid grid-cols-1 gap-4 lg:hidden pb-10">
              {filteredOffers.map((offer, index) => (
                <div
                  key={offer.id}
                  style={{ animationDelay: `${index * 80}ms` }}
                  className="bg-zinc-900/90 backdrop-blur-md p-5 rounded-[2rem] border border-zinc-800/80 hover:border-emerald-500/40 shadow-xl micro-interaction transition-all animate-spring-in group"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      {renderLogo(offer.logo, offer.name)}
                      <div>
                        <h4 className="font-bold text-zinc-100 text-base">{offer.name}</h4>
                        <p className="text-xs font-bold text-zinc-500 uppercase tracking-wider mt-0.5">
                          #{offer.id}
                        </p>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-bold uppercase tracking-wider">
                      {offer.category}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-6 bg-zinc-950/60 p-3.5 rounded-2xl border border-zinc-800/80">
                    <div>
                      <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Payout</p>
                      <p className="text-lg font-black text-emerald-400">{offer.payout}</p>
                    </div>
                    <button
                      onClick={() => navigate(`/offers/${offer.id}`)}
                      className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-bold shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95"
                    >
                      View Details
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-zinc-900/80 backdrop-blur-md border border-zinc-800/80 rounded-[2.5rem] shadow-2xl overflow-hidden animate-spring-in">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-950/60 border-b border-zinc-800/80">
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Offer ID</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Offer Details</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-wider text-zinc-500">Category</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-wider text-zinc-500 text-center">Payout</th>
                      <th className="px-8 py-6 text-xs font-bold uppercase tracking-wider text-zinc-500 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    {filteredOffers.map((offer, index) => (
                      <tr
                        key={offer.id}
                        style={{ animationDelay: `${index * 60}ms` }}
                        className="group hover:bg-emerald-500/[0.03] transition-all duration-300 animate-spring-in"
                      >
                        <td className="px-8 py-6">
                          <span className="text-sm font-bold text-zinc-500 group-hover:text-zinc-300 transition-colors">
                            #{offer.id}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center gap-4">
                            {renderLogo(offer.logo, offer.name)}
                            <span className="text-[15px] font-bold text-zinc-100 group-hover:text-emerald-400 transition-colors">
                              {offer.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className="px-3.5 py-1.5 bg-zinc-950 text-zinc-400 border border-zinc-800 group-hover:border-emerald-500/20 rounded-xl text-xs font-bold uppercase tracking-wider transition-colors">
                            {offer.category}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className="text-[16px] font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-xl inline-block shadow-sm shadow-emerald-500/5 group-hover:scale-105 transition-transform duration-300">
                            {offer.payout}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-right">
                          <button
                            onClick={() => navigate(`/offers/${offer.id}`)}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 active:scale-95 rounded-xl text-xs font-bold transition-all shadow-lg shadow-emerald-500/10 micro-interaction"
                          >
                            View Details
                            <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Offers;